import { useEffect, useMemo, useState } from 'react'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  Coin,
  PrinterFill,
} from 'react-bootstrap-icons'
import findProductsSvg from '../../assets/illustration/find-products.svg'
import { CustomButton } from '../../components/CustomButton'
import Navbar from '../../components/navbar/navbar'
import { ModalDevolucao } from './ModalDevolucao'
import { Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { CustomSpinner } from '../../components/CustomSpinner'
import TrocasDevolucoesService from '../../services/database/TrocasDevolucoesService'
import { formatDate } from '../../utils/formatDate'
import { formatPrice } from '../../utils/formatPrice'
import { format } from 'date-fns'
import { useOrderTable } from '../../hooks/useOrderTable'
import './styles.css'

const ITEMS_PER_PAGE = 10

export function TrocasDevolucoes() {
  const [refunds, setRefunds] = useState([])
  const [selectedRefunds, setSelectedRefunds] = useState([])
  const [isLoadingRefunds, setIsLoadingRefunds] = useState(true)
  const [isModalRefundOpen, setIsModalRefundOpen] = useState(false)
  const [selectedTermFilter, setSelectedTermFilter] = useState('pes_razao')
  const [filterSearch, setFilterSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(0)

  const {
    sortedArray: sortedRefunds,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(refunds, 'id')

  // Filtra sobre o array completo ordenado (não só a página atual)
  const filteredRefunds = useMemo(() => {
    if (!filterSearch.trim()) return sortedRefunds
    return sortedRefunds.filter((refund) =>
      String(refund[selectedTermFilter] ?? '')
        .toLowerCase()
        .includes(filterSearch.toLowerCase()),
    )
  }, [filterSearch, sortedRefunds, selectedTermFilter, orderType.field, orderType.type])

  // Reseta para página 0 quando o filtro muda
  useEffect(() => {
    setCurrentPage(0)
  }, [filterSearch, selectedTermFilter])

  const pageCount = Math.ceil(filteredRefunds.length / ITEMS_PER_PAGE)

  const currentPageItems = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE
    return filteredRefunds.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredRefunds, currentPage])

  // Helpers de seleção
  const isSelected = (refund) => selectedRefunds.some((r) => r.id === refund.id)

  const toggleRefund = (refund) => {
    setSelectedRefunds((prev) =>
      isSelected(refund)
        ? prev.filter((r) => r.id !== refund.id)
        : [...prev, refund],
    )
  }

  const isAllPageSelected =
    currentPageItems.length > 0 &&
    currentPageItems.every((r) => isSelected(r))

  const isSomePageSelected =
    currentPageItems.some((r) => isSelected(r)) && !isAllPageSelected

  const toggleAllPage = (checked) => {
    if (checked) {
      setSelectedRefunds((prev) => {
        const news = currentPageItems.filter((r) => !prev.some((p) => p.id === r.id))
        return [...prev, ...news]
      })
    } else {
      setSelectedRefunds((prev) =>
        prev.filter((r) => !currentPageItems.some((p) => p.id === r.id)),
      )
    }
  }

  // Estatísticas
  const stats = useMemo(() => {
    const totalValor = refunds.reduce((s, r) => s + (r.valor_total ?? 0), 0)
    const comSaldo = refunds.filter((r) => r.acrescimo_saldo === 'S').length
    return { totalValor, comSaldo }
  }, [refunds])

  async function loadRefunds() {
    setIsLoadingRefunds(true)
    try {
      const data = await TrocasDevolucoesService.getAll()
      setRefunds(data)
    } catch (error) {
      console.error(error)
      toast.error('Erro ao buscar as devoluções')
    } finally {
      setIsLoadingRefunds(false)
    }
  }

  async function genProof() {
    if (selectedRefunds.length === 0) return

    const { default: pdfmake } = await import('pdfmake/build/pdfmake')
    // const { default: pdfFonts } = await import('pdfmake/build/vfs_fonts')
    // pdfmake.vfs = pdfFonts.pdfMake.vfs

    const currentDate = format(new Date(), "dd/MM/yyyy 'às' HH:mm")

    const refundItems = selectedRefunds.map((refund) => [
      { text: String(refund.id_pedido), style: 'tableRow' },
      { text: `${refund.sku_produto} - ${refund.descricao}`, style: 'tableRow' },
      { text: String(refund.quantidade), style: 'tableRow' },
      { text: formatPrice(refund.valor_unitario), style: 'tableRow' },
      { text: formatPrice(refund.valor_total), style: 'tableRow' },
      { text: refund.acrescimo_saldo === 'S' ? 'Sim' : 'Não', style: 'tableRow' },
      { text: formatDate(refund.data_devolucao), style: 'tableRow' },
      { text: refund.nomefuncionario, style: 'tableRow' },
    ])

    const saldoTotal = selectedRefunds.reduce(
      (acc, item) => (item.acrescimo_saldo === 'S' ? acc + item.valor_total : acc),
      0,
    )
    const totalGeral = selectedRefunds.reduce((acc, item) => acc + item.valor_total, 0)

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      content: [
        { text: 'Comprovante de Devolução', style: 'mainInfo' },
        {
          text: `Gerado em ${currentDate} por ${localStorage.username ?? 'Usuário'}`,
          style: 'baseText',
          margin: [0, 0, 0, 10],
        },
        {
          layout: 'lightHorizontalLines',
          table: {
            headerRows: 1,
            widths: [55, 'auto', 50, 60, 60, 55, 65, 70],
            body: [
              [
                { text: 'Pedido', style: 'tableHeader' },
                { text: 'Produto', style: 'tableHeader' },
                { text: 'Qtd', style: 'tableHeader' },
                { text: 'Vl. Unitário', style: 'tableHeader' },
                { text: 'Vl. Total', style: 'tableHeader' },
                { text: 'Acrésc.', style: 'tableHeader' },
                { text: 'Data Devolução', style: 'tableHeader' },
                { text: 'Funcionário', style: 'tableHeader' },
              ],
              ...refundItems,
            ],
          },
          margin: [0, 0, 0, 16],
        },
        {
          columns: [
            {
              text: [
                { text: 'Itens selecionados: ', style: 'baseText' },
                { text: String(selectedRefunds.length), bold: true, fontSize: 10 },
              ],
            },
            {
              text: [
                { text: 'Total devolvido: ', style: 'baseText' },
                { text: formatPrice(totalGeral), bold: true, fontSize: 10 },
              ],
              alignment: 'center',
            },
            {
              text: [
                { text: 'Saldo/Crédito em Haver: ', style: 'baseText' },
                { text: formatPrice(saldoTotal), bold: true, fontSize: 10, color: '#3B6D11' },
              ],
              alignment: 'right',
            },
          ],
          margin: [0, 0, 0, 0],
        },
      ],
      styles: {
        mainInfo: { fontSize: 16, bold: true, margin: [0, 0, 0, 4] },
        baseText: { fontSize: 9, color: '#555555' },
        tableHeader: { fontSize: 8, bold: true, fillColor: '#f4f4f4' },
        tableRow: { fontSize: 8 },
      },
      defaultStyle: { font: 'Roboto' },
    }

    pdfmake.createPdf(docDefinition).open()
  }

  useEffect(() => {
    loadRefunds()
  }, [])

  // Paginação: gera os botões com ellipsis
  function getPaginationItems() {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i)
    const items = []
    const delta = 2
    const left = currentPage - delta
    const right = currentPage + delta

    for (let i = 0; i < pageCount; i++) {
      if (i === 0 || i === pageCount - 1 || (i >= left && i <= right)) {
        items.push(i)
      } else if (i === left - 1 || i === right + 1) {
        items.push('...')
      }
    }
    return items
  }

  return (
    <>
      <Navbar />
      <div className="mt-page" style={{ background: '#ffffff', padding: '1.5rem' }}>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.25rem' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 2 }}>Trocas e Devoluções</h1>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
              {refunds.length} registro{refunds.length !== 1 ? 's' : ''} no total
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <CustomButton color="#0d6efd" onClick={() => setIsModalRefundOpen(true)}>
              <Coin /> Adicionar Devolução
            </CustomButton>
            <CustomButton color="#b019ed" onClick={loadRefunds}>
              <ArrowClockwise /> Recarregar
            </CustomButton>
            <CustomButton
              onClick={genProof}
              isDisabled={selectedRefunds.length === 0}
              title={selectedRefunds.length === 0 ? 'Selecione ao menos um item' : `Gerar comprovante (${selectedRefunds.length} selecionado${selectedRefunds.length > 1 ? 's' : ''})`}
            >
              <PrinterFill />
              {selectedRefunds.length > 0
                ? `Gerar Comprovante (${selectedRefunds.length})`
                : 'Gerar Comprovante'}
            </CustomButton>
          </div>
        </div>

        {/* Cards de estatísticas */}
        {!isLoadingRefunds && refunds.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
            {[
              { label: 'Total de devoluções', value: refunds.length, color: '#185FA5' },
              { label: 'Valor total devolvido', value: formatPrice(stats.totalValor), color: '#333' },
              { label: 'Com acréscimo de saldo', value: stats.comSaldo, color: '#3B6D11' },
              { label: 'Itens selecionados', value: selectedRefunds.length, color: '#BA7517' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#f7f7f8', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Conteúdo principal */}
        {isLoadingRefunds ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
            <CustomSpinner color="#0d6efd" />
          </div>
        ) : refunds.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 0' }}>
            <img src={findProductsSvg} style={{ width: 320, opacity: 0.7 }} alt="Sem dados" />
            <p style={{ marginTop: 16, color: '#888' }}>Nenhuma devolução registrada</p>
          </div>
        ) : (
          <>
            {/* Barra de pesquisa */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <Form.Select
                value={selectedTermFilter}
                onChange={(e) => setSelectedTermFilter(e.target.value)}
                style={{ width: 170, fontSize: 13 }}
              >
                <option value="pes_razao">Cliente</option>
                <option value="sku_produto">SKU</option>
                <option value="id">ID Devolução</option>
                <option value="id_pedido">Nº Pedido</option>
                <option value="nomefuncionario">Funcionário</option>
              </Form.Select>

              <Form.Control
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Pesquisar..."
                style={{ flex: 1, minWidth: 200, fontSize: 13 }}
              />

              {filterSearch && (
                <button
                  onClick={() => setFilterSearch('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13 }}
                >
                  ✕ Limpar
                </button>
              )}

              <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
                {filteredRefunds.length} resultado{filteredRefunds.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Tabela */}
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed', minWidth: 1050 }}>
                <colgroup>
                  <col style={{ width: 40 }} />
                  <col style={{ width: 60 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 240 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 55 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 130 }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f7f7f8', borderBottom: '2px solid #e8e8e8' }}>
                    <th style={thStyle()}>
                      <Form.Check
                        checked={isAllPageSelected}
                        ref={(el) => { if (el) el.indeterminate = isSomePageSelected }}
                        onChange={(e) => toggleAllPage(e.target.checked)}
                        title="Selecionar todos da página"
                      />
                    </th>
                    {[
                      { field: 'id',             label: 'ID' },
                      { field: 'id_pedido',       label: 'Pedido' },
                      { field: 'descricao',       label: 'Produto' },
                      { field: 'pes_razao',       label: 'Cliente' },
                      { field: 'quantidade',      label: 'Qtd',        align: 'center' },
                      { field: 'valor_unitario',  label: 'Vl. Unit.',  align: 'right' },
                      { field: 'valor_total',     label: 'Vl. Total',  align: 'right' },
                      { field: null,              label: 'Acrésc.',    align: 'center' },
                      { field: 'data_devolucao',  label: 'Data' },
                      { field: 'nomefuncionario', label: 'Funcionário' },
                    ].map(({ field, label, align }) => (
                      <th
                        key={label}
                        onClick={field ? () => handleOrganize(field) : undefined}
                        style={thStyle(align, !!field)}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          {label}
                          {field && (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1 }}>
                              <CaretUp size={9} color={check(field, 'asc')} />
                              <CaretDown size={9} color={check(field, 'desc')} />
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentPageItems.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center', padding: '2.5rem', color: '#aaa', fontSize: 13 }}>
                        Nenhum resultado para "{filterSearch}"
                      </td>
                    </tr>
                  ) : (
                    currentPageItems.map((refund, idx) => (
                      <tr
                        key={refund.id}
                        onClick={() => toggleRefund(refund)}
                        style={{
                          background: isSelected(refund)
                            ? 'rgba(13,110,253,0.07)'
                            : idx % 2 === 0 ? '#fff' : '#fafafa',
                          cursor: 'pointer',
                          transition: 'background 0.1s',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                        onMouseEnter={(e) => { if (!isSelected(refund)) e.currentTarget.style.background = '#f0f4ff' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = isSelected(refund) ? 'rgba(13,110,253,0.07)' : idx % 2 === 0 ? '#fff' : '#fafafa' }}
                      >
                        <td style={tdStyle('center')} onClick={(e) => e.stopPropagation()}>
                          <Form.Check checked={isSelected(refund)} onChange={() => toggleRefund(refund)} />
                        </td>
                        <td style={tdStyle()}>{refund.id}</td>
                        <td style={tdStyle()}>{refund.id_pedido}</td>
                        <td style={tdStyle()}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${refund.sku_produto} - ${refund.descricao}`}>
                            <span style={{ color: '#888', fontSize: 12 }}>{refund.sku_produto}</span>
                            {' · '}
                            {refund.descricao}
                          </span>
                        </td>
                        <td style={tdStyle()}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={refund.pes_razao}>
                            {refund.pes_razao}
                          </span>
                        </td>
                        <td style={tdStyle('center')}>{refund.quantidade}</td>
                        <td style={tdStyle('right')}>{formatPrice(refund.valor_unitario)}</td>
                        <td style={{ ...tdStyle('right'), fontWeight: 600 }}>{formatPrice(refund.valor_total)}</td>
                        <td style={tdStyle('center')}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 600,
                            background: refund.acrescimo_saldo === 'S' ? '#EAF3DE' : '#f1f1f1',
                            color: refund.acrescimo_saldo === 'S' ? '#27500A' : '#888',
                          }}>
                            {refund.acrescimo_saldo === 'S' ? 'Sim' : 'Não'}
                          </span>
                        </td>
                        <td style={tdStyle()}>{formatDate(refund.data_devolucao)}</td>
                        <td style={tdStyle()}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={refund.nomefuncionario}>
                            {refund.nomefuncionario}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#888' }}>
                  Mostrando {currentPage * ITEMS_PER_PAGE + 1}–{Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredRefunds.length)} de {filteredRefunds.length}
                </span>

                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <PagBtn disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
                    ‹ Anterior
                  </PagBtn>

                  {getPaginationItems().map((item, idx) =>
                    item === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ padding: '0 6px', color: '#aaa', fontSize: 13 }}>…</span>
                    ) : (
                      <PagBtn
                        key={item}
                        active={item === currentPage}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item + 1}
                      </PagBtn>
                    ),
                  )}

                  <PagBtn disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => p + 1)}>
                    Próxima ›
                  </PagBtn>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isModalRefundOpen && (
        <ModalDevolucao
          isOpen={isModalRefundOpen}
          loadRefunds={loadRefunds}
          onClose={() => setIsModalRefundOpen(false)}
        />
      )}
    </>
  )
}

// Helpers de estilo para th e td
function thStyle(align = 'left', clickable = false) {
  return {
    padding: '10px 12px',
    textAlign: align,
    fontSize: 11.5,
    fontWeight: 600,
    color: '#555',
    whiteSpace: 'nowrap',
    cursor: clickable ? 'pointer' : 'default',
    userSelect: 'none',
    letterSpacing: '0.02em',
  }
}

function tdStyle(align = 'left') {
  return {
    padding: '9px 12px',
    textAlign: align,
    fontSize: 13,
    color: '#222',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  }
}

// Componente auxiliar de botão de paginação
function PagBtn({ children, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px',
        fontSize: 12,
        borderRadius: 6,
        border: '1px solid',
        borderColor: active ? '#0d6efd' : '#dee2e6',
        background: active ? '#0d6efd' : '#fff',
        color: active ? '#fff' : disabled ? '#aaa' : '#333',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 600 : 400,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  )
}